import React, { useContext, useEffect, useState } from 'react'
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import moment from 'moment'
import {
  CalendarMonth,
  AccessTime,
  AirportShuttle,
  Business,
  CalendarViewWeek,
  DirectionsCar,
  LocalOffer,
  MedicalServices,
  InfoOutlined,
  SwapHoriz,
  EventAvailable,
  EventBusy,
} from '@mui/icons-material'
import API from '../../services/API'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../App'
import OfferShift from './OfferShift'
import { formatTravelSummary, getTravelMinutes } from '../../helpers/shiftTravel'
import { formatMoney } from '../../helpers/currency'
import { describePtsDuty, getShiftListType } from '../../helpers/shiftListType'

const statusConfig = ({ shiftConfirmed, userHasApplied, shift, isOwn }) => {
  if (shift?.availableForReassignment && shift?.assignedTo) {
    return {
      label: isOwn ? 'Offered for Swap' : 'Available for Swap',
      color: '#7f33cc',
      icon: <SwapHoriz />,
    }
  }

  if (shiftConfirmed) {
    return {
      label: 'Shift Booked',
      color: '#22bb33',
      icon: <EventAvailable />,
    }
  }

  if (userHasApplied) {
    return {
      label: 'Pending Approval',
      color: '#F28B1F',
      icon: <InfoOutlined />,
    }
  }

  return {
    label: 'Shift Open',
    color: '#E32525',
    icon: <EventBusy />,
  }
}

const DetailTile = ({ icon, label, value, subValue, xs = 6 }) => (
  <Grid item xs={12} sm={xs}>
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <Box sx={{ color: 'secondary.main', mt: 0.2 }}>{icon}</Box>
        <Box minWidth={0}>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={800}
            textTransform="uppercase"
            letterSpacing={0.4}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={850}
            sx={{ wordBreak: 'break-word' }}
          >
            {value || '—'}
          </Typography>
          {subValue && (
            <Typography variant="caption" color="text.secondary">
              {subValue}
            </Typography>
          )}
        </Box>
      </Stack>
    </Paper>
  </Grid>
)

const ShiftApplicationPopup = ({
  shift,
  onClose,
  loadAvailableShifts,
  disableApplication,
  requestRestrictions,
  shiftConfirmed,
}) => {
  const { user } = useContext(UserContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const { setToast } = useContext(GlobalContext)

  const [openOfferShift, setOpenOfferShift] = useState(false)
  const [conflictingShifts, setConflictingShifts] = useState([])
  const [conflictingShiftApplications, setConflictingShiftApplications] =
    useState([])

  const { shiftBookingPermissions } = orgSettings

  useEffect(() => {
    API.get(
      `/conflicting-shifts?start=${moment(shift.start).toISOString()}&end=${moment(shift.end).toISOString()}`,
    ).then((res) => {
      if (res.status === 200) {
        setConflictingShifts(
          (res.data.conflictingShifts ?? []).filter((s) => s._id !== shift._id),
        )
        setConflictingShiftApplications(
          (res.data.conflictingShiftApplications ?? []).filter(
            (a) => a._id !== shift._id,
          ),
        )
      }
    })
  }, [shift])

  const eventIsTenative = ['Potential', 'Interested', 'Quote Sent'].includes(
    shift?.event?.status,
  )

  const listType = getShiftListType(shift)
  const isPTS = listType === 'PTS'
  // Null when the duty isn't populated (e.g. rows from /my-shifts), in which
  // case we can't say anything about the vehicle or customer.
  const duty = isPTS ? describePtsDuty(shift.ptsDuty) : null

  // Your own swap offer is listed on the board as confirmation it's been
  // offered - never something you can apply for.
  const userAlreadyAssigned =
    !!shift.assignedTo &&
    String(shift.assignedTo?._id ?? shift.assignedTo) ===
      String(user?.employee?._id)

  const userHasApplied =
    shift?.applications &&
    shift.applications.some(
      (a) => a.status === 'Pending' && a.employee._id === user.employee._id,
    )

  const canBook =
    !userAlreadyAssigned &&
    (!shiftBookingPermissions?.preventIfHasShiftAssigned ||
      !conflictingShifts?.length) &&
    (!shiftBookingPermissions?.preventIfHasShiftApplicationInProgress ||
      !conflictingShiftApplications?.length)

  const status = statusConfig({
    shiftConfirmed,
    userHasApplied,
    shift,
    isOwn: userAlreadyAssigned,
  })

  const handleApply = () => {
    API.post('/shift-application', {
      shift: shift._id,
      employee: user.employee._id,
      eventStatusAtTimeOfApplication: shift?.event?.status,
    }).then((res) => {
      if (res.status === 200) {
        setToast({ severity: 'success', message: 'Application Submitted' })
        onClose()
        loadAvailableShifts()
      } else if (res.status === 400 && res.data === 'ownShift') {
        setToast({
          severity: 'error',
          message: 'You cannot apply for a shift you are already assigned to',
        })
        onClose()
      } else if (res.status === 400 && res.data === 'alreadyAssigned') {
        setToast({
          severity: 'error',
          message: 'Sorry, this shift is no longer available',
        })
        onClose()
        loadAvailableShifts()
      } else if (
        res.status === 403 &&
        res.data === 'expiredMandatoryTraining'
      ) {
        setToast({
          severity: 'error',
          message:
            'You cannot request this shift because your mandatory training is incomplete or expired.',
        })
        onClose()
      } else if (
        res.status === 403 &&
        res.data === 'occupationalHealthDue'
      ) {
        setToast({
          severity: 'error',
          message:
            'You cannot request this shift because your occupational health screening is outstanding.',
        })
        onClose()
      }
    })
  }

  const handleCancel = () => {
    const applicationID = shift.applications.find(
      (a) => a.employee._id === user.employee._id,
    )?._id

    if (!applicationID) return

    API.delete(`/shift-application?applicationID=${applicationID}`).then(
      (res) => {
        if (res.status === 200) {
          setToast({ message: 'Application Cancelled' })
          onClose()
          loadAvailableShifts()
        }
      },
    )
  }

  const handleCancelShiftSwapRequest = () => {
    API.post('/shift-application/cancel-swap-request', {
      shiftID: shift._id,
    }).then((res) => {
      if (res.status === 200) {
        onClose()
        loadAvailableShifts()
        setToast({ message: 'Shift Swap Request Cancelled' })
      }
    })
  }

  return (
    <Box
      component={Paper}
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '92%', sm: 560 },
        maxHeight: '86vh',
        overflow: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: '0 28px 80px rgba(15,23,42,0.25)',
        outline: 'none',
      }}
    >
      <Box
        sx={{
          p: 2,
          background: `linear-gradient(135deg, ${status.color} 0%, ${status.color}dd 100%)`,
          color: '#fff',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.18)' }}>
              {status.icon}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={950} color="white">
                {status.label}
              </Typography>
              <Typography variant="body2" color="rgba(255,255,255,0.85)">
                {moment(shift.start).format('dddd, Do MMMM YYYY')}
              </Typography>
            </Box>
          </Stack>

          <Chip
            label={listType}
            sx={{
              bgcolor: 'rgba(255,255,255,0.18)',
              color: '#fff',
              fontWeight: 900,
            }}
          />
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ p: 2 }}>
        {shift.availableForReassignment &&
          shift.assignedTo &&
          !userAlreadyAssigned && (
            <Alert severity="info">
              {user?.employee?.teams?.includes('Personnel')
                ? `${shift?.assignedTo?.userAccount?.firstName} ${shift?.assignedTo?.userAccount?.lastName} has offered this shift as available for a swap.`
                : 'A colleague has requested to swap this shift.'}
            </Alert>
          )}

        <Box>
          <Grid container spacing={1.5}>
            <DetailTile
              icon={<CalendarMonth fontSize="small" />}
              label="Date"
              value={moment(shift.start).format('DD MMM YYYY')}
            />
            <DetailTile
              icon={<AccessTime fontSize="small" />}
              label="Time"
              value={`${moment(shift.start).format('HH:mm')} - ${moment(shift.end).format('HH:mm')}`}
            />
            <DetailTile
              icon={<MedicalServices fontSize="small" />}
              label="Clinical Role"
              value={shift.clinicalRole}
            />
            {shift.isGroup && (
              <DetailTile
                icon={<InfoOutlined fontSize="small" />}
                label="Vacancies"
                value={`${shift.filledCount} of ${shift.quantity} filled`}
                subValue={`${shift.openCount} still open`}
              />
            )}
            {shift.payRate > 0 && (
              <DetailTile
                icon={<LocalOffer fontSize="small" />}
                label="Rate"
                value={`${formatMoney(shift.payRate, orgSettings?.currency)}/${shift.hourlyRate ? 'hr' : 'shift'}`}
              />
            )}
            {(shift.bonuses ?? []).length > 0 && (
              <DetailTile
                icon={<LocalOffer fontSize="small" />}
                label="Fixed Bonuses"
                value={shift.bonuses
                  .map(
                    (bonus) =>
                      `${formatMoney(bonus.amount, orgSettings?.currency)}${bonus.label ? ` ${bonus.label}` : ''}`,
                  )
                  .join(', ')}
              />
            )}
            {getTravelMinutes(shift.travel) > 0 && (
              <DetailTile
                icon={<LocalOffer fontSize="small" />}
                label="Paid Travel"
                value={formatTravelSummary(
                  shift.travel,
                  orgSettings?.currency ?? '£',
                )}
              />
            )}
            {shift?.event?.title && (
              <DetailTile label="Event" value={shift.event.title} xs={12} />
            )}
            {isPTS && !shift.ptsDuty && (
              <DetailTile
                icon={<AirportShuttle fontSize="small" />}
                label="PTS Duty"
                value="Not yet attached to a duty"
                subValue="Vehicle and customer will be confirmed once the duty is planned"
                xs={12}
              />
            )}
            {duty && (
              <>
                <DetailTile
                  icon={<AirportShuttle fontSize="small" />}
                  label="PTS Duty"
                  value={
                    duty.dutyRef
                      ? `Duty ${duty.dutyRef}`
                      : 'Patient Transport duty'
                  }
                  subValue={
                    duty.baseLocationName
                      ? `From ${duty.baseLocationName}`
                      : undefined
                  }
                />
                <DetailTile
                  icon={<DirectionsCar fontSize="small" />}
                  label="Vehicle"
                  value={duty.vehicleLabel}
                  subValue={
                    duty.hasVehicle
                      ? undefined
                      : 'A vehicle may be allocated nearer the time'
                  }
                />
                <DetailTile
                  icon={<Business fontSize="small" />}
                  label="Customer"
                  value={duty.customerLabel}
                  subValue={duty.customerCaption ?? undefined}
                  xs={12}
                />
              </>
            )}
            {listType === 'Rota' && shift?.roster?.title && (
              <DetailTile
                icon={<CalendarViewWeek fontSize="small" />}
                label="Rota"
                value={shift.roster.title}
                xs={12}
              />
            )}
            {shift.eventAssignment && (
              <DetailTile
                label="Event Role"
                value={shift.eventAssignment}
                xs={12}
              />
            )}
            {shift.shiftNote && (
              <DetailTile label="Shift Note" value={shift.shiftNote} xs={12} />
            )}
          </Grid>
        </Box>

        <Divider />

        <Stack spacing={1}>
          {eventIsTenative && (
            <Alert severity="info">
              This event has not yet been confirmed. This shift is provisional
              only.
            </Alert>
          )}

          {conflictingShifts?.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>You are already assigned to:</AlertTitle>
              {conflictingShifts.map((x) => (
                <Typography key={x._id} variant="body2">
                  {moment(x.start).format('Do MMM HH:mm')} -{' '}
                  {moment(x.end).format('Do MMM HH:mm')}
                </Typography>
              ))}
            </Alert>
          )}

          {conflictingShiftApplications?.length > 0 && (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>You have already applied for:</AlertTitle>
              {conflictingShiftApplications.map((x) => (
                <Typography key={x._id} variant="body2">
                  {moment(x.start).format('Do MMM HH:mm')} -{' '}
                  {moment(x.end).format('Do MMM HH:mm')}
                </Typography>
              ))}
            </Alert>
          )}

          {requestRestrictions?.length > 0 && !userHasApplied && (
            <Alert severity="error">
              <AlertTitle>You cannot apply because:</AlertTitle>
              <ul style={{ marginTop: 0, marginBottom: 0 }}>
                {requestRestrictions.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            </Alert>
          )}
        </Stack>

        {shift.event && (
          <Button
            variant="outlined"
            fullWidth
            color="anPurple"
            onClick={() =>
              window.open('/event-info/' + shift.event._id, '_blank')
            }
            sx={{ borderRadius: 2, fontWeight: 800 }}
          >
            View Event Info
          </Button>
        )}

        {!disableApplication && (
          <Stack spacing={1}>
            {canBook ? (
              <Button
                fullWidth
                disabled={requestRestrictions.length > 0 && !userHasApplied}
                variant="contained"
                color={userHasApplied ? 'warning' : 'success'}
                onClick={userHasApplied ? handleCancel : handleApply}
                sx={{ borderRadius: 2, fontWeight: 900, color: 'white' }}
              >
                {userHasApplied ? 'Cancel Application' : 'Request Shift'}
              </Button>
            ) : userHasApplied ? (
              <Button
                fullWidth
                variant="contained"
                color="warning"
                onClick={handleCancel}
                sx={{ borderRadius: 2, fontWeight: 900 }}
              >
                Cancel Application
              </Button>
            ) : null}
          </Stack>
        )}

        {userAlreadyAssigned && shift.availableForReassignment && (
          <Alert severity="info">
            <Stack spacing={1}>
              <Typography variant="body2">
                You have offered this shift for a swap and your managers have
                been told. You are still assigned and responsible for it until
                a manager accepts a colleague&apos;s application or reassigns
                it.
              </Typography>
              <Button
                onClick={handleCancelShiftSwapRequest}
                variant="outlined"
                color="primary"
                sx={{ borderRadius: 2, fontWeight: 800 }}
              >
                Cancel shift swap request
              </Button>
            </Stack>
          </Alert>
        )}

        {shiftConfirmed &&
          !shift.availableForReassignment &&
          moment(shift.start).isAfter(moment()) && (
            <Button
              variant="contained"
              fullWidth
              onClick={() => setOpenOfferShift(true)}
              color="anBlue"
              sx={{ borderRadius: 2, fontWeight: 900 }}
            >
              Offer Shift as Available
            </Button>
          )}
      </Stack>

      <OfferShift
        open={openOfferShift}
        onClose={() => {
          setOpenOfferShift(false)
          onClose()
        }}
        loadAvailableShifts={loadAvailableShifts}
        shiftID={shift._id}
      />
    </Box>
  )
}

export default ShiftApplicationPopup
